import React from "react";
import { Row, Col, Tag, Typography, Space, Empty, Collapse } from "antd";
import dayjs from "dayjs";
import { mailStatusOptions } from "../utils";

const MailCustomerTab = ({ judgeData, mailData, statusEms }) => {
  const judge = judgeData?.judge?.[0];
  const mail = mailData || {};

  // ── helpers ──
  const fmtDate = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "-");

  const replyTag = (statusVal) => {
    const opt = mailStatusOptions.find(
      (o) => Number(o.value) === Number(statusVal),
    );
    if (!opt)
      return <Tag style={{ borderRadius: 10, margin: 0 }}>ยังไม่ตอบกลับ</Tag>;
    return (
      <Tag color={opt.color} style={{ borderRadius: 10, margin: 0 }}>
        {opt.icon}
        {opt.label}
      </Tag>
    );
  };

  const emsText = (codeVal) =>
    statusEms?.find((s) => Number(s.code) === Number(codeVal))?.status ?? "-";

  const sortByNewest = (list = []) =>
    [...list].sort(
      (a, b) =>
        dayjs(b.createdDate || b.datetime).valueOf() -
        dayjs(a.createdDate || a.datetime).valueOf(),
    );

  const field = (label, node) => (
    <Col key={label} xs={24} sm={12} md={8}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Typography.Text>
      <div
        style={{
          fontSize: 14,
          color: "#1a365d",
          fontWeight: 500,
          marginTop: 2,
        }}
      >
        {node}
      </div>
    </Col>
  );

  const renderMailItem = (item, idx, accent) => (
    <div
      key={item.id}
      style={{
        border: "1px solid #eef0f3",
        borderLeft: `4px solid ${accent}`,
        borderRadius: 10,
        padding: "12px 16px",
        background: "#fff",
        marginBottom: 10,
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <Space size={8}>
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: accent,
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
            }}
          >
            {idx + 1}
          </span>
          <Typography.Text strong style={{ fontSize: 15 }}>
            {item.customerFullname || "-"}
          </Typography.Text>
          <Tag color={item.customerTypeId === 1 ? "geekblue" : "blue"}>
            {item.customerTypeId === 1 ? "ผู้ค้ำ" : "ผู้เช่าซื้อ"}
          </Tag>
        </Space>
        {replyTag(item.statusReply)}
      </div>

      <Row gutter={[12, 10]}>
        {field(
          "เลขพัสดุ (ส่ง)",
          <Tag color="blue" style={{ margin: 0 }}>
            {item.parcelNo || "-"}
          </Tag>,
        )}
        {field(
          "เลขพัสดุ (ตอบกลับ)",
          <Tag color="cyan" style={{ margin: 0 }}>
            {item.parcelNoResponse || "-"}
          </Tag>,
        )}
        {field("วันที่ส่ง", fmtDate(item.datetime || item.dateParcelNo))}
        {field("วันที่ตอบกลับ", fmtDate(item.dateReply))}
        {field("สถานะ (EMS)", emsText(item.statusPostWeb))}
        {field("วันนำจ่าย", fmtDate(item.datePostWeb))}
        {field(
          "ยืนยันเจอจดหมายตอบกลับ",
          item.flagConfirm ? `✓ ${fmtDate(item.dateConfirm)}` : "-",
        )}
      </Row>
    </div>
  );

  // เนื้อหา 1 section (เรียงใหม่→เก่า)
  const sectionBody = (list = [], accent) => {
    const sorted = sortByNewest(list);
    return sorted.length > 0 ? (
      sorted.map((item, idx) => renderMailItem(item, idx, accent))
    ) : (
      <Empty
        description="ไม่มีข้อมูล"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ margin: "8px 0" }}
      />
    );
  };

  // หัวข้อ section (จุดสี + ชื่อ + จำนวน)
  const sectionLabel = (title, count, accent) => (
    <Space size={8}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: accent,
          display: "inline-block",
        }}
      />
      <Typography.Text strong style={{ fontSize: 15 }}>
        {title}
      </Typography.Text>
      <Tag color={accent} style={{ borderRadius: 10, margin: 0 }}>
        {count} รายการ
      </Tag>
    </Space>
  );

  // ── ทำยอม (judge) — การ์ดเดี่ยว ──
  const renderTamYom = () => {
    if (!judge) {
      return (
        <Empty
          description="ไม่มีข้อมูลทำยอม"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }
    const accent = "#1677ff";
    return (
      <div
        style={{
          border: "1px solid #eef0f3",
          borderLeft: `4px solid ${accent}`,
          borderRadius: 10,
          padding: "12px 16px",
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        <Row gutter={[12, 10]}>
          {field(
            "เลขพัสดุ",
            <Tag color="blue" style={{ margin: 0 }}>
              {judge.parcelNo || "-"}
            </Tag>,
          )}
          {field("วันที่บันทึก", fmtDate(judge.dateParcelNo))}
          {field("สถานะ (EMS)", emsText(judge.statusPostWeb))}
          {field(
            "วันที่นำส่ง",
            judge.datePostWeb === "0001-01-01T00:00:00Z" || !judge.datePostWeb
              ? "-"
              : fmtDate(judge.datePostWeb),
          )}
        </Row>
      </div>
    );
  };

  const demand = mail.workDemandGuaranter || [];
  const cancel = mail.workCancelContract || [];
  const repurchase = mail.letterCarRepurchase || [];

  // รายการ Collapse แต่ละ section
  const items = [
    {
      key: "tamyom",
      label: sectionLabel(
        "ส่งเอกสาร ทำยอม",
        judgeData?.flagLoanNew === 1 ? 1 : 0,
        "#1677ff",
      ),
      children: renderTamYom(),
    },
    {
      key: "demand",
      label: sectionLabel("ส่งเอกสาร บอกกล่าว", demand.length, "#722ed1"),
      children: sectionBody(demand, "#722ed1"),
    },
    {
      key: "cancel",
      label: sectionLabel("ส่งเอกสาร บอกเลิก", cancel.length, "#fa8c16"),
      children: sectionBody(cancel, "#fa8c16"),
    },
    {
      key: "repurchase",
      label: sectionLabel(
        "ส่งเอกสาร ใช้สิทธิ์ซื้อรถคืน",
        repurchase.length,
        "#13c2c2",
      ),
      children: sectionBody(repurchase, "#13c2c2"),
    },
  ];

  // เปิดอัตโนมัติเฉพาะ section ที่มีข้อมูล (+ ทำยอมเสมอ)
  const defaultOpen = [
    "tamyom",
    demand.length ? "demand" : null,
    cancel.length ? "cancel" : null,
    repurchase.length ? "repurchase" : null,
  ].filter(Boolean);

  return (
    <Collapse
      items={items}
      defaultActiveKey={defaultOpen}
      expandIconPosition="end"
      bordered={false}
      style={{ background: "transparent" }}
    />
  );
};

export default MailCustomerTab;
